import ko from "./ko.json";
import en from "./en.json";

export type Locale = "ko" | "en";
type Bundle = Record<string, string>;

const BUNDLES: Record<Locale, Bundle> = { ko, en };
const SUPPORTED: Locale[] = ["ko", "en"];
const STORAGE_KEY = "cm.locale";

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ko" || saved === "en") return saved;
  } catch {
    /* localStorage unavailable */
  }
  const nav = (navigator.language || "en").toLowerCase();
  return nav.startsWith("ko") ? "ko" : "en";
}

type Listener = (locale: Locale) => void;

class I18n {
  private locale: Locale = "en";
  private listeners = new Set<Listener>();

  init(): void {
    this.locale = detectLocale();
  }

  get current(): Locale {
    return this.locale;
  }

  get supported(): readonly Locale[] {
    return SUPPORTED;
  }

  set(locale: Locale): void {
    if (locale === this.locale) return;
    this.locale = locale;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = locale;
    for (const fn of this.listeners) fn(locale);
  }

  toggle(): void {
    this.set(this.locale === "ko" ? "en" : "ko");
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Translate a key. Falls back to English, then to the key itself. */
  t(key: string, params?: Record<string, string | number>): string {
    let s = BUNDLES[this.locale][key] ?? BUNDLES.en[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        s = s.replace(`{${k}}`, String(v));
      }
    }
    return s;
  }
}

export const i18n = new I18n();
export const t = (key: string, params?: Record<string, string | number>): string =>
  i18n.t(key, params);
